import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';

import {
  Button,
  Checkbox,
  Modal,
  Radio,
  RadioGroup
} from '@salesforce/design-system-react';
import { Input } from '@salesforce/design-system-react';
import * as Util from '../../../common/utilities/';
import { trimAndStrip } from '../../../utilities/stringUtils';

import './AttributeEditModal.scss';

// TODO: don't allow empty string
class AttributeEditModal extends Component {
  constructor(props) {
    super(props);
    this.modalName = 'attributeEdit';
    this.anchors = {
      addLink: React.createRef()
    };
    this.state = {
      valueHasBlurred: false
    };
  }

  componentDidUpdate(prevProps) {
    if (
      Util.propChanged(
        prevProps,
        this.props,
        'modals.attributeEdit.data.addedValue',
        true
      )
    ) {
      this.anchors.addLink.current.scrollIntoView({ behavior: 'smooth' });
      this.props.actions.updateModal(this.modalName, { addedValue: false });
    }
  }

  addValueRow() {
    const attribute = { ...this.attribute() };
    attribute.values = [...attribute.values, ''];
    this.props.actions.updateModal(this.modalName, {
      attribute,
      addedValue: true
    });
  }

  deleteValueRow(idx) {
    const attribute = { ...this.attribute() };
    const values = [...attribute.values];
    values.splice(idx, 1);
    this.props.actions.updateModal(this.modalName, {
      attribute: { ...attribute, values }
    });
  }

  updateValueRow(idx, value) {
    const attribute = {
      ...this.attribute(),
      values: [...this.attribute().values]
    };
    attribute.values[idx] = value;
    this.props.actions.updateModal(this.modalName, { attribute });
  }

  updateAttributeProp(prop, value) {
    const attribute = {
      ...this.attribute(),
      values: [...this.attribute().values]
    };
    attribute[prop] = value;
    this.props.actions.updateModal(this.modalName, { attribute });
  }

  renderValueRow(value, idx) {
    const { formatMessage } = this.props.intl;
    const editDisabled = this.attribute().editDisabled;
    const deleteDisabled = this.values().length <= 1 || editDisabled;

    return (
      <li className="attribute-value-row" key={`li-value-${idx}`}>
        <Input
          value={value}
          disabled={editDisabled}
          onChange={(event, { value }) => this.updateValueRow(idx, value)}
          onBlur={() => this.setState({ valueHasBlurred: true })}
          errorText={
            !!this.noValuesWarning()
              ? formatMessage({ id: this.getI18nID('required') })
              : null
          }
        />
        <Button
          className="delete-value-button"
          iconCategory="utility"
          iconName="delete"
          iconVariant="border-filled"
          variant="icon"
          disabled={deleteDisabled}
          onClick={() => this.deleteValueRow(idx)}
        />
      </li>
    );
  }

  noValuesWarning() {
    if (this.props.attributeType === 'profile') {
      return false;
    }
    if (!this.attribute().allow_new) {
      const hasValue = trimAndStrip(this.attribute().values).length;
      if (
        !hasValue &&
        (this.state.valueHasBlurred || this.originalAttribute().allow_new)
      ) {
        return true;
      }
    }
  }

  renderRequired() {
    return (
      <abbr className="slds-required" title="required">
        *
      </abbr>
    );
  }

  renderModal() {
    const { formatMessage } = this.props.intl;

    const newValuesRadio = (
      <RadioGroup
        className="new-values-radio"
        onChange={event =>
          this.updateAttributeProp('allow_new', event.target.value === 'true')
        }
      >
        <Radio
          key="allowNewValues"
          id="allowNewValues"
          labels={{
            label: formatMessage({ id: this.getI18nID('allowNewValues') })
          }}
          value="true"
          checked={!!this.attribute().allow_new}
          variant="base"
        />
        <Radio
          key="disallowNewValues"
          id="disallowNewValues"
          labels={{
            label: formatMessage({ id: this.getI18nID('disallowNewValues') })
          }}
          value="false"
          checked={!this.attribute().allow_new}
          variant="base"
        />
      </RadioGroup>
    );

    const isRequiredCheckbox = (
      <Checkbox
        className="asset-attribute-checkbox"
        labels={{
          label: formatMessage({ id: this.getI18nID('isRequired') })
        }}
        onChange={(_event, { checked }) =>
          this.updateAttributeProp('required', checked)
        }
        checked={this.attribute().required}
      />
    );

    const isMultiValueCheckbox = (
      <Checkbox
        className="asset-attribute-checkbox"
        labels={{
          label: formatMessage({ id: this.getI18nID('isMultiValue') })
        }}
        onChange={(_event, { checked }) =>
          this.updateAttributeProp('multi_value', checked)
        }
        checked={!!this.attribute().multi_value}
      />
    );

    const addNewValuesLink = (
      <span
        className="add-value-link"
        onClick={this.addValueRow.bind(this)}
        ref={this.anchors.addLink}
      >
        <FormattedMessage id={this.getI18nID('addValue')} />
      </span>
    );

    return (
      <div className="attribute-modal-content">
        <h2>
          <FormattedMessage id={this.getI18nID('detailsHeader', false, true)} />
        </h2>
        <div className="attribute-details-row attribute-modal-input">
          <Input
            label={formatMessage({ id: this.getI18nID('name') })}
            value={this.attribute().name || ''}
            required={true}
            disabled={
              this.mode() === 'edit' || this.props.attributeType === 'profile'
            }
            onChange={(_event, { value }) =>
              this.updateAttributeProp('name', value)
            }
          />
          {this.props.attributeType === 'asset' ? isRequiredCheckbox : null}
          {this.props.attributeType === 'asset' ? isMultiValueCheckbox : null}
        </div>
        <h2>
          <FormattedMessage id={this.getI18nID('valuesHeader')} />
        </h2>
        {this.props.attributeType === 'asset' ? newValuesRadio : null}
        <span className="attribute-value-description">
          {!!this.attribute().allow_new ? null : this.renderRequired()}
          <FormattedMessage id={this.getI18nID('valuesDescription')} />
        </span>
        <ul className="attribute-value-list">
          {this.values().map(this.renderValueRow.bind(this))}
        </ul>
        {this.attribute().editDisabled ? null : addNewValuesLink}
      </div>
    );
  }

  renderFooter() {
    const { formatMessage } = this.props.intl;

    /**
     * Cancel
     */
    const cancelLabel = formatMessage({ id: 'common.actions.cancel' });
    const cancelButton = (
      <Button
        label={cancelLabel}
        variant="neutral"
        onClick={this.closeModal.bind(this)}
      />
    );

    /**
     * Save
     */
    const saveID = `${this.mode()}-asset-attribute`;
    const saveLabel = formatMessage({ id: 'common.actions.save' });
    const saveButton = (
      <Button
        id={saveID}
        label={saveLabel}
        disabled={!this.canSave()}
        variant="brand"
        onClick={() => this.props.onSave(this.attribute())}
      />
    );

    return (
      <div className="footer-button-actions">
        {cancelButton}
        {saveButton}
      </div>
    );
  }

  dirty() {
    const current = this.attribute();
    const original = this.originalAttribute();
    const fields = ['values', 'required', 'allow_new', 'multi_value'];
    if (this.props.attributeType === 'asset') {
      fields.unshift('name');
    }
    return fields.some(
      key => JSON.stringify(current[key]) !== JSON.stringify(original[key])
    );
  }

  canSave() {
    if (!!this.noValuesWarning()) {
      return false;
    }

    // attribute not editable (e.g. profile attributes of boolean data type must have True and False values)
    if (this.attribute().editDisabled) {
      return false;
    }

    // edit mode
    if (this.attribute().uuid || this.props.attributeType === 'profile') {
      return this.dirty();
    } else {
      // create mode
      return this.attribute().name?.length;
    }
  }

  attribute() {
    return this.props.modals[this.modalName]?.data?.attribute;
  }
  originalAttribute() {
    return this.props.modals[this.modalName]?.data?.original;
  }

  values() {
    return this.attribute().values;
  }

  mode() {
    return this.attribute()?.uuid ? 'edit' : 'create';
  }

  getI18nID(suffix, modeBased, attributeBased) {
    const mode = modeBased ? this.mode() + '.' : '';
    const attribute = attributeBased ? this.props.attributeType : 'common';
    return `components.ecs.attributes.modal.${attribute}.${mode}${suffix}`;
  }

  closeModal() {
    this.props.actions.closeModal(this.modalName);
  }

  render() {
    const { formatMessage } = this.props.intl;
    const isOpen = this.props.modals[this.modalName]?.isOpen;
    const modalTitle = formatMessage({
      id: this.getI18nID('title', true, true)
    });

    if (!isOpen) {
      return null;
    }
    return (
      <Modal
        heading={modalTitle}
        footer={this.renderFooter()}
        onRequestClose={this.closeModal.bind(this)}
        dismissOnClickOutside={false}
        disableClose={true}
        isOpen={isOpen || false}
      >
        {this.renderModal()}
      </Modal>
    );
  }
}

AttributeEditModal.propTypes = {
  actions: PropTypes.object.isRequired,
  flags: PropTypes.object,
  modals: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  attributeType: PropTypes.oneOf(['asset', 'profile']).isRequired,
  intl: intlShape.isRequired
};

export default injectIntl(AttributeEditModal);
